import React, { useEffect } from 'react';
import { WarningCircle } from '@phosphor-icons/react';

/**
 * Lightweight confirmation dialog for destructive / meaningful actions.
 * Controlled: renders nothing when `open` is false.
 *
 * Usage:
 *   <ConfirmDialog
 *     open={pendingChallenge !== null}
 *     title="Send challenge?"
 *     message={`Are you sure you want to challenge ${name}?`}
 *     confirmLabel="Challenge"
 *     onConfirm={() => { runAction(); setPendingChallenge(null); }}
 *     onCancel={() => setPendingChallenge(null)}
 *   />
 */
export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  onConfirm,
  onCancel,
  busy = false,
  testId = 'confirm-dialog',
}) {
  // Lock page scroll while dialog is open so the background doesn't move
  // underneath and the user can always reach the buttons.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const confirmClass = destructive
    ? 'bg-red-600 hover:bg-red-700 text-white'
    : 'bg-[#015e3f] hover:bg-[#014d34] text-white';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in overflow-y-auto overscroll-contain"
      role="dialog"
      aria-modal="true"
      data-testid={testId}
      onClick={onCancel}
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm my-auto bg-[#fbfaf8] rounded-3xl border border-[#e3dcd1] shadow-2xl p-5 animate-fade-in-up max-h-[calc(100dvh-2rem)] overflow-y-auto"
      >
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${destructive ? 'bg-red-50' : 'bg-[#c2a251]/15'}`}>
            <WarningCircle size={22} weight="duotone" className={destructive ? 'text-red-600' : 'text-[#c2a251]'} />
          </div>
          <div className="flex-1 min-w-0">
            {title && <div className="font-serif text-xl text-[#013523] tracking-tight">{title}</div>}
            {message && <div className="text-sm text-[#2a332c] mt-1 leading-snug">{message}</div>}
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            data-testid={`${testId}-cancel`}
            className="flex-1 bg-white text-[#013523] border border-[#e3dcd1] rounded-full px-4 py-2.5 text-sm font-medium active:scale-95 transition-transform hover:bg-[#f0ece3] disabled:opacity-50 disabled:pointer-events-none"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            data-testid={`${testId}-confirm`}
            className={`flex-1 rounded-full px-4 py-2.5 text-sm font-medium active:scale-95 transition-transform disabled:opacity-60 disabled:pointer-events-none ${confirmClass}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
